import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { CountryDto, ScoreRecipientInstitutionDto, ScoreRecipientRegDto, UserAccountRegDto } from 'src/app/core/dtos/dto';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { RegistrationService } from 'src/app/core/services/registration.service';
import { TypeaheadFocusComponent } from '../typeahead-focus/typeahead-focus.component';
import { Observable, Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { TestSessionForRegistrationDto } from 'src/app/core/dtos/dto';
import { selectRegTestSession } from 'src/app/state/reg-data.selectors';
import { BrandEnum } from 'src/app/core/dtos/enum';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';

@Component({
  selector: 'app-score-preferences-institution',
  templateUrl: './score-preferences-institution.component.html',
  styleUrls: ['./score-preferences-institution.component.scss']
})


export class ScorePreferencesInstitutionComponent implements OnInit {
  @Output() institutionSubmit = new EventEmitter<ScoreRecipientRegDto>();

  @Input() countries: CountryDto[];
  @Input() testTypeId: number;
  @Input() scoreRecipientReg: ScoreRecipientRegDto;
  @Input() submitted: boolean;
  @Input() isWebDelivery: boolean;
  @Input() showIRCCOnNewInstitutions: boolean;
  @Input() regInfo: UserAccountRegDto;

  @ViewChild(TypeaheadFocusComponent, { static: false }) typeaheadFocusComponent: TypeaheadFocusComponent;

  userForm: FormGroup;
  institutionGroups: string[];
  activeInstitutions: ScoreRecipientInstitutionDto[];
  listOfInstitutions: string[] = [];
  institutionsNameAndId: { [key: number]: string } = {};
  scoreRecipientName: string;
  testSessionSubscription: Subscription;
  testSessionInformation$: Observable<TestSessionForRegistrationDto>;

  academicLevels = ["Undergraduate", "Graduate", "Other"];

  brandId: BrandEnum;
  regId: number;

  get f() { return this.userForm.controls; }

  constructor(
    private registrationService: RegistrationService,
    private formBuilder: FormBuilder,
    private toastService: HotToastService,
    private store: Store,
    private configHelper: ConfigHelper
  ) { }

  ngOnInit(): void {
    if (this.isWebDelivery == null) {           // if it is from test registration
      this.testSessionInformation$ = this.store.pipe(select(selectRegTestSession));
      this.testSessionSubscription = this.testSessionInformation$
        .subscribe(testSession => {
          this.isWebDelivery = testSession?.isWebDelivery === true;
        });
    }

    this.brandId = this.configHelper.getBrandId();

    //this.regInfo is undefined when it comes from registration
    //it has value when it comes from Update Score Recipient action
    this.regId = 0;
    if (this.regInfo != null) {
      this.regId = this.regInfo.regId;
    };

    this.getActiveRecipientInstitutions(this.scoreRecipientReg.country.id, this.testTypeId, this.regId);
    this.userForm = this.formBuilder.group({
      country: [this.scoreRecipientReg.country.id, Validators.required],
      studentNumber: [this.scoreRecipientReg.studentNumber],
      academicLevel: [this.scoreRecipientReg.academicLevel, Validators.required],
      scoreRecipient: [this.scoreRecipientReg.institutionId, Validators.required],
    });
    if (this.scoreRecipientReg.other === true) {
      this.scoreRecipientReg.scoreRecipient = null;
    } else {
      this.scoreRecipientReg.institutionName = null;
      this.scoreRecipientReg.mailingAddress = null;
    }
  }

  public get BrandEnum() {
    return BrandEnum;
  }

  emitScoreRecipientReg() {
    const countryResult = this.countries.find(x => x.id === Number(this.f.country.value));
    this.scoreRecipientReg.country = countryResult;
    this.scoreRecipientReg.studentNumber = this.userForm.controls.studentNumber.value;
    const institutionId = Object.keys(this.institutionsNameAndId).filter(k => this.institutionsNameAndId[k] === this.typeaheadFocusComponent.model)[0];
    const institutionResult = this.activeInstitutions.find(x => x.id.toString() === institutionId);

    if (institutionResult) {
      this.scoreRecipientReg.scoreRecipient = institutionResult;
      this.scoreRecipientReg.institutionName = institutionResult.institutionName;
      this.scoreRecipientReg.institutionId = institutionResult.id;
    } else {
      this.scoreRecipientReg.scoreRecipient = null;
      this.scoreRecipientReg.institutionName = null;
      this.scoreRecipientReg.institutionId = null;
    }
    this.scoreRecipientReg.academicLevel = this.f.academicLevel.value;
    this.scoreRecipientReg.scoreRecipients = this.activeInstitutions;

    this.institutionSubmit.emit(this.scoreRecipientReg);

    this.userForm.patchValue({
      studentNumber: "",
      academicLevel: "",
      scoreRecipient: null,
    });
    this.scoreRecipientName = null;
  }

  getActiveRecipientInstitutions(countryId: number, testTypeId: number, regId: number) {
    this.registrationService.getActiveScoreRecipients(countryId, testTypeId, regId, this.isWebDelivery).subscribe(
      result => {
        this.activeInstitutions = result.content;
        this.setIrccStatus();
        this.listOfInstitutions = [];
        this.institutionGroups = Array.from(new Set(this.activeInstitutions.map(({ groupName }) => groupName)));
        this.activeInstitutions.forEach((data) => {
          if (data.institutionName.includes("(" + data.groupName + ")")) {
            this.institutionsNameAndId[data.id] = data.institutionName;
            this.listOfInstitutions.push(data.institutionName);
          } else {
            this.institutionsNameAndId[data.id] = data.institutionName + " (" + data.groupName + ")";
            this.listOfInstitutions.push(data.institutionName + " (" + data.groupName + ")");
          }
        });
        this.scoreRecipientName = this.institutionsNameAndId[this.scoreRecipientReg.institutionId];
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  setIrccStatus() {
    var irccInstitutionId = 1022;
    if (this.brandId === BrandEnum.CAEL) {
      irccInstitutionId = 1088;
    }
    if (this.activeInstitutions.length > 0 && this.regId == 0 && !this.showIRCCOnNewInstitutions) {
      // removing IRCC from the dropdown of new institution
      // the input value "showIRCCOnNewInstitutions" is set only when it comes from reg flow (ie regId = 0) or reg has at least one score recipient
      // otherwise, regId != 0 and the activeInstitutions check/filtered out IRCC in backend
      this.activeInstitutions = this.activeInstitutions.filter(i => i.id !== irccInstitutionId);
    }
  }

  setDirty() {
    this.userForm.controls.academicLevel.markAsDirty();
    this.userForm.controls.scoreRecipient.markAsDirty();
  }
}
